import { type Coords } from "leaflet";
import type { ResourceParams } from "../../db/resource";

// Half the circumference of the earth in EPSG:3857 meters.
const originShift = 20037508.342789244;
const tileSize = 256;

/**
 * URL of a single WMS tile for a layer synced from the new Datenportal.
 *
 * Tiles are requested through the portal's WMS proxy rather than from the WMS
 * service directly — the WebView would block http:// services, credentials and
 * services without CORS headers. The same function is used for rendering and
 * for precaching, so both produce identical URLs (= cache keys).
 */
export function portalWmsTileUrl(
  baseUrl: string,
  mapLayerId: string,
  subLayerName: string,
  { x, y, z }: Pick<Coords, "x" | "y" | "z">,
): string {
  const size = (2 * originShift) / Math.pow(2, z);
  const minX = -originShift + x * size;
  const maxY = originShift - y * size;
  const bbox = [minX, maxY - size, minX + size, maxY].join(",");

  const params = new URLSearchParams({
    VERSION: "1.3.0",
    LAYERS: subLayerName,
    STYLES: "",
    FORMAT: "image/png",
    TRANSPARENT: "true",
    CRS: "EPSG:3857",
    WIDTH: String(tileSize),
    HEIGHT: String(tileSize),
    BBOX: bbox,
  });

  return `${baseUrl}/api/device/sync/map-layers/${encodeURIComponent(mapLayerId)}/wms?${params}`;
}

export function portalWmsResourceParams(portalKey: string): ResourceParams {
  return {
    // WMS data (e.g. building plans) changes more often than base map tiles;
    // stale tiles are still shown and refreshed in the background.
    overrideExpiry: 7 * 24 * 60 * 60 * 1000,
    requestOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
  };
}
